import React, { Fragment, useEffect, useRef, useState } from 'react';
import { useSnackbars } from '../../../hooks/snackbar.hook';
import { detachedContract, getContracts } from '../../../services/contracts.service';
import ListContractsUi from '../../../components/user-interfaces/list-contracts/list-contracts.ui';
import { ContractDto } from '../../../services/dtos/contract.dto';
import { useNavigate } from 'react-router-dom';
import { useConfirm } from 'material-ui-confirm';
import { showAxiosMessageError } from '../../../services/error-message.service';

/**
 * Composant de la page de liste des contrats
 * L'utilisateur est forcément connecté et pour avoir accès à cette page, il possède obligatoirement au moins un contrat
 * @constructor
 */
export default function ListContractsPage() {
    //Permet de savoir si on est passé une fois dans l'useEffect
    const dataFetchedRef = useRef(false);
    //Use confirm dialog
    const confirm = useConfirm();
    //Snackbars
    const showMessage = useSnackbars();
    //Navigate
    const navigate = useNavigate();

    //Déclaration du loader pour l'affichage des Skeleton
    const [loading, setLoading] = useState<boolean>(true);
    const [contracts, setContracts] = useState<Array<ContractDto>>([]);

    // Récupère les données du back permettant de construire la page
    useEffect(
        () => {
            if (dataFetchedRef.current) {
                return;
            }
            dataFetchedRef.current = true;
            getContracts()
                .then(result => {
                    //On parcourt les contrats que s'il y a une liste de contrat
                    //Sinon c'est qu'il n'y a pas de contrat
                    if (Array.isArray(result)) {
                        // Formatte tous les contrats
                        const formattedResult = result.map(contract => {
                            // Si le champ '@id' existe et contient "/rattachement_contrats/"
                            if (contract['@id'] && typeof contract['@id'] === 'string') {
                                // Supprime "/rattachement_contrats/"
                                contract['@id'] = contract['@id'].replace('/rattachement_contrats/', '');
                            }
                            return contract;
                        });
                        setContracts(formattedResult);
                    } else {
                        showMessage.infoAlert('Aucun contrat rattaché');
                    }
                    setLoading(false);
                })
                .catch(error => {
                    //Affichage du message d'erreur si la requete ne récupère rien
                    console.error('Erreur lors de la récupération des contrats de la personne connectée', error);
                    showAxiosMessageError(error, showMessage);
                });
        },

        //https://stackoverflow.com/questions/55840294/how-to-fix-missing-dependency-warning-when-using-useeffect-react-hook
        // eslint-disable-next-line react-hooks/exhaustive-deps
        [navigate]
    );

    /**
     * Permet de détacher un contrat.
     */
    const handleClickDettacheContract = (event: string) => {
        if (event !== undefined) {
            confirm({
                title: 'Êtes-vous certain de vouloir effectuer cette action ?',
                description: `Le contrat sélectionné sera détaché de votre compte.`,
                cancellationText: 'Annuler',
            })
                .then(() => {
                    detachedContract(event)
                        .then(message => {
                            showMessage.successAlert(message);
                            let updateContracts = contracts.filter(contract => contract['@id'] !== event);
                            setContracts(updateContracts);
                            if (updateContracts.length === 0) {
                                showMessage.infoAlert('Aucun contrat rattaché');
                            }
                        })
                        .catch(error => {
                            console.error('Erreur lors de la récupération du contrat');
                            showAxiosMessageError(error, showMessage);
                        });
                })
                .catch(() => console.warn('Suppression annulée.'));
        }
    };

    // On renvoie la liste des contrats et l'état de chargement de la page
    return (
        <Fragment>
            <ListContractsUi
                contracts={contracts}
                loading={loading}
                handleDettachContract={handleClickDettacheContract}
            />
        </Fragment>
    );
}
